import Box from "@mui/material/Box"
import Grid from "@mui/material/Grid"
import Typography from "@mui/material/Typography"

import FileUploaderV2 from "components/FileUploaderV2"
import MuiAutoComplete from "components/MuiAutocomplete"
import MuiTextField from "components/MuiTextField"
import { ProjectFormPageProps } from "components/ProjectForm"
import ProjectFormFooter from "../../../components/ProjectFormFooter"
import useDemolitionsBasicDetails, { EProjectBasicDetailsFields } from "./useDemolitionsBasicDetails"

const DemolitionBasicDetails = (props: ProjectFormPageProps) => {
    const { states, functions } = useDemolitionsBasicDetails(props)
    return (
        <>
            <Box
                display={"flex"}
                flexDirection={"column"}
                gap={4}
                className="custom-scrollbar"
                maxHeight={"65vh"}
                padding={"var(--mui-modal-padding)"}
                sx={{
                    overflowY: "auto",
                }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">Project Name*</Typography>
                            <MuiTextField
                                name={EProjectBasicDetailsFields.PROJECT_NAME}
                                placeholder="Enter Project Name"
                                size="large"
                                value={states.formik.values[EProjectBasicDetailsFields.PROJECT_NAME]}
                                onChange={states.formik.handleChange}
                                onBlur={states.formik.handleBlur}
                                error={functions.hasError(EProjectBasicDetailsFields.PROJECT_NAME)}
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="label">Deal Done by (Business POC)*</Typography>
                                <Typography
                                    variant="label"
                                    fontSize={"10px"}
                                    textTransform={"none"}
                                    onClick={functions.handleAssignToMe}
                                    sx={{
                                        textDecoration: "underline",
                                        textUnderlineOffset: "2px",
                                        fontWeight: "400",
                                        color: "#0430C0",
                                        cursor: "pointer",
                                    }}>
                                    Assign to me
                                </Typography>
                            </Box>
                            <MuiAutoComplete
                                disablePortal={false}
                                disableClearable
                                size="large"
                                placeholder="Select Name"
                                options={states.dealDoneByUsersOptions}
                                value={states.formik.values[EProjectBasicDetailsFields.DEAL_DONE_BY] || null}
                                onChange={value =>
                                    states.formik.setFieldValue(EProjectBasicDetailsFields.DEAL_DONE_BY, value)
                                }
                                error={functions.hasError(EProjectBasicDetailsFields.DEAL_DONE_BY)}
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">Expected Project Closure Date*</Typography>
                            <MuiTextField
                                name={EProjectBasicDetailsFields.PROJECT_CLOSURE_DATE}
                                placeholder="Select date"
                                type="date"
                                size="large"
                                value={states.formik.values[EProjectBasicDetailsFields.PROJECT_CLOSURE_DATE]}
                                onChange={states.formik.handleChange}
                                onBlur={states.formik.handleBlur}
                                error={functions.hasError(EProjectBasicDetailsFields.PROJECT_CLOSURE_DATE)}
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">Program</Typography>
                            <MuiAutoComplete
                                disablePortal={false}
                                size="large"
                                options={states.programsOptions}
                                placeholder="Select Program"
                                disabled={states.programsLoading}
                                value={states.formik.values[EProjectBasicDetailsFields.PROGRAM] || null}
                                onChange={value =>
                                    states.formik.setFieldValue(EProjectBasicDetailsFields.PROGRAM, value)
                                }
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">Project Category*</Typography>
                            <MuiAutoComplete
                                size="large"
                                options={states.categoryOptions}
                                placeholder="Select Project Category"
                                loading={states.isCategoriesFetching}
                                value={states.formik.values[EProjectBasicDetailsFields.CATEGORY] || null}
                                onChange={value =>
                                    states.formik.setFieldValue(EProjectBasicDetailsFields.CATEGORY, value)
                                }
                                error={functions.hasError(EProjectBasicDetailsFields.CATEGORY)}
                            />
                        </Box>
                    </Grid>
                    <Grid size={6}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">Upload Deal Docs</Typography>
                            <Box display="flex" gap={1}>
                                <MuiAutoComplete
                                    disablePortal={false}
                                    disableClearable
                                    size="large"
                                    sx={{
                                        flex: 1,
                                    }}
                                    options={states.fileTypes}
                                    placeholder="Select File Type"
                                    value={states.selectedFileType}
                                    onChange={value =>
                                        functions.handleDealDocsFileTypeChange(
                                            value as { id: string; title: string } | null
                                        )
                                    }
                                />
                                <Box flex={1}>
                                    <FileUploaderV2
                                        disabled={!states.selectedFileType}
                                        associatedId={
                                            states.project_id && states.selectedFileType ? states.project_id : undefined
                                        }
                                        fileType={states.selectedFileType?.id}
                                        onUpload={
                                            states.project_id && states.selectedFileType
                                                ? functions.handleDealDocsUpload
                                                : undefined
                                        }
                                        onChange={!states.project_id ? functions.handleDealDocsChange : undefined}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">Demolition Reason*</Typography>
                            <MuiAutoComplete
                                disablePortal={false}
                                size="large"
                                options={states.demolitionReasonOptions}
                                placeholder="Select Demolition Reason"
                                value={states.formik.values[EProjectBasicDetailsFields.DEMOLITION_REASON] || null}
                                onChange={value =>
                                    states.formik.setFieldValue(EProjectBasicDetailsFields.DEMOLITION_REASON, value)
                                }
                                error={functions.hasError(EProjectBasicDetailsFields.DEMOLITION_REASON)}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            <ProjectFormFooter
                handleCloseForm={props.handleCloseForm}
                isLoading={states.isAddingProjectTicket}
                handleSubmit={() => states.formik.handleSubmit()}
            />
        </>
    )
}

export default DemolitionBasicDetails
